import { markSessionExpired } from "./session";

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: { error?: string; details?: unknown } | null,
  ) {
    super(body?.error ?? `HTTP ${status}`);
  }
}
/** Network unreachable (offline, captive portal, DNS…). The request can be retried later. */
export class OfflineError extends Error {
  constructor() {
    super("offline");
  }
}
/** The app's session is missing or expired (401). */
export class SessionExpiredError extends Error {
  constructor() {
    super("session_expired");
  }
}

export type ApiInit = Omit<RequestInit, "body"> & { json?: unknown; body?: BodyInit | null; signal?: AbortSignal };

/**
 * All API calls go through here.
 *  - `X-Requested-With` is required by the server on writes (CSRF).
 *  - 401 is the app's own session gone: sign in again in place (login sheet).
 *  - `redirect: "manual"`: the API never redirects, so a redirect is an error, never followed.
 */
export async function apiFetch(path: string, init: ApiInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set("X-Requested-With", "XMLHttpRequest");
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  let body = init.body;
  if (init.json !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(init.json);
  }
  let res: Response;
  try {
    res = await fetch(path, { ...init, headers, body, redirect: "manual", credentials: "same-origin" });
  } catch (e) {
    if ((e as Error)?.name === "AbortError") throw e;
    throw new OfflineError();
  }
  if (res.status === 401) {
    markSessionExpired();
    throw new SessionExpiredError();
  }
  const ct = res.headers.get("content-type") ?? "";
  if (!res.ok) {
    let parsed: ApiError["body"] = null;
    try {
      parsed = ct.includes("json") ? await res.json() : null;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, parsed);
  }
  return res;
}

async function json<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, init?: ApiInit) => apiFetch(path, init).then(json<T>),
  put: <T>(path: string, data: unknown) => apiFetch(path, { method: "PUT", json: data }).then(json<T>),
  post: <T>(path: string, data: unknown) => apiFetch(path, { method: "POST", json: data }).then(json<T>),
  patch: <T>(path: string, data: unknown) => apiFetch(path, { method: "PATCH", json: data }).then(json<T>),
  del: (path: string) => apiFetch(path, { method: "DELETE" }).then(() => undefined),
};

export const fileUrl = (key: string) => `/api/files/${key}`;
